import "react-datepicker/dist/react-datepicker.css";
import { useState, useEffect, useCallback, memo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
// @ts-expect-error: ignoring as it is existing js file
import DatePicker from "react-datepicker";
// Need to use Button and ButtonGroup from MUI for design consistency
import { Input, InputAdornment, Button, ButtonGroup, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import { GridRenderCellParams } from "@mui/x-data-grid";
import moment from "moment";
import { debounce, startCase } from "lodash";
import { FormControl, Typography, Grid, IconButton, Stack, styled } from "@financeable-com-au/financeable-ui";
import {
  DEFAULT_SORT_FIELD,
  INVOICE_BILLING_MONTHS,
  INVOICE_STATUS,
  INVOICE_TYPES,
  ROUTES,
  dateFormat,
} from "@/constants";
import { saveValue, setInvoiceFilterDate, invoiceStateSelector, getAllInvoices } from "@/store/slices/invoiceSlice";
import { InvoiceRow, GetAllInvoicesFnArgs, IInvoiceSlice, IInvoiceState } from "@/store/slices/types/invoiceSliceTypes";
import { AppDispatch } from "@/store/store";
import { fCurrencyDecimal } from "@/utils/formatNumber";
import InvoiceStatusCell from "./InvoiceStatusCell";
// @ts-expect-error: ignoring as it is existing js file
import DataGridTable from "../../dataGridTable/DataGridTable";

const SearchWrapper = styled("div")({
  display: "flex",
  flexWrap: "wrap",
});

const SearchFormControl = styled(FormControl)(({ theme }) => ({
  margin: theme.spacing(1),
  width: "61ch",
}));

const InvoiceList = memo(() => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const pageSizeRef = useRef(10);
  const invoiceStatusFilterRef = useRef<string>("all");
  const invoiceTypeFilterRef = useRef<string>(INVOICE_TYPES.custom);
  const invoiceState = useSelector<IInvoiceSlice, IInvoiceState>(invoiceStateSelector);
  const { invoices, gettingInvoices, page, filterDate } = invoiceState;
  const [rows, setRows] = useState<InvoiceRow[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [rowCountState, setRowCountState] = useState(invoices?.totalDocs || 0);
  const [params, setParams] = useState({ offset: 1, limit: 10, sort: DEFAULT_SORT_FIELD } as {
    offset: number;
    limit: number;
    sort: { field: string; sort: "asc" | "desc" };
  });
  const invoicesStatusFilters = [
    "all",
    INVOICE_STATUS.draft,
    INVOICE_STATUS.open,
    INVOICE_STATUS.overdue,
    INVOICE_STATUS.paid,
  ];
  // const invoicesTypeFilters = ["all", INVOICE_TYPES.custom, INVOICE_TYPES.auto];

  const handle = {
    getAllInvoicesFn: ({
      cursor = 1,
      search,
      status = invoiceStatusFilterRef.current || "all",
      type = invoiceTypeFilterRef.current || INVOICE_TYPES.custom,
      startDate,
      endDate,
      isClearDateSelection = false,
    }: GetAllInvoicesFnArgs) => {
      const sort = params?.sort || DEFAULT_SORT_FIELD;
      const updatedStartDate = startDate ? startDate : moment(filterDate?.startDate).format("YYYY-MM-DD");
      const updatedEndDate = endDate ? endDate : moment(filterDate?.endDate).format("YYYY-MM-DD");

      dispatch(
        getAllInvoices({
          cursor,
          limit: pageSizeRef?.current || 10,
          search,
          status,
          type,
          sort,
          startDate:
            isClearDateSelection || !updatedStartDate || updatedStartDate === "Invalid date"
              ? undefined
              : updatedStartDate,
          endDate:
            isClearDateSelection || !updatedEndDate || updatedEndDate === "Invalid date" ? undefined : updatedEndDate,
        }),
      );
    },
    handleSearch: (searchTerm: string) => {
      (searchTerm.length >= 2 || searchTerm.length <= 0) &&
        handle.getAllInvoicesFn({ cursor: invoices?.page, search: searchTerm });
    },
    handleStatusFilterChange: async (option: string) => {
      handle.getAllInvoicesFn({
        cursor: 1,
        search: searchTerm,
        status: option,
      });
      invoiceStatusFilterRef.current = option;
    },
    handleTypeFilterChange: async (option: string) => {
      handle.getAllInvoicesFn({
        cursor: 1,
        search: searchTerm,
        type: option,
      });
      invoiceTypeFilterRef.current = option;
    },
    onChangeDatePicker: (dates: [Date | null, Date | null]) => {
      const startDate = moment(dates?.[0]).format("YYYY-MM-DD");
      const endDate = moment(dates?.[1]).format("YYYY-MM-DD");

      dispatch(setInvoiceFilterDate({ startDate: dates?.[0], endDate: dates?.[1] }));

      if ((dates[0] && dates[1]) || (!dates[0] && !dates[1])) {
        handle.getAllInvoicesFn({
          cursor: 1,
          search: searchTerm,
          startDate: startDate === "Invalid date" ? undefined : startDate,
          endDate: endDate === "Invalid date" ? undefined : endDate,
          isClearDateSelection: dates?.[0] === null && dates?.[1] === null ? true : false,
        });
      }
    },
    navigateToInvoicePage: (invoiceId: string) => {
      navigate(ROUTES.ADMIN.INVOICE_DETAIL.replace(":invoiceId", String(invoiceId)));
    },
    updateInvoiceStatusRow: ({ id, status }: { id: string; status: string }) => {
      setRows((prevRows) => prevRows.map((row) => (row.id === id ? { ...row, status: startCase(status) } : row)));
    },
  };

  const invoicesColumns = [
    {
      field: "createdAt",
      headerName: "Date",
      type: "string",
      width: 120,
      editable: false,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<{ date: string; time: string }>) => (
        <div>
          <Typography style={{ fontSize: "14px" }}>{params.value?.date}</Typography>
          <Typography textColor="neutral.500" style={{ fontSize: "11px" }}>
            {params.value?.time}
          </Typography>
        </div>
      ),
    },
    {
      field: "organisation",
      headerName: "Organisation",
      width: 220,
      sortable: false,
      filterable: false,
    },
    {
      field: "tenantName",
      headerName: "Tenant",
      width: 220,
      sortable: false,
      filterable: false,
    },
    {
      field: "total",
      headerName: "Amount",
      width: 140,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<number>) => (
        <Typography style={{ fontSize: "14px" }}>{fCurrencyDecimal(params.value ?? 0)}</Typography>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 140,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => (
        <InvoiceStatusCell
          params={params}
          invoiceId={String(params.row?.id)}
          onStatusUpdated={handle.updateInvoiceStatusRow}
        />
      ),
    },
    // Enable when we open custom and auto filters
    // {
    //   field: "type",
    //   headerName: "Type",
    //   width: 120,
    //   sortable: false,
    //   filterable: false,
    // },
    {
      field: "month",
      headerName: "Month",
      width: 120,
      sortable: false,
      filterable: false,
    },
    {
      field: "year",
      headerName: "Year",
      width: 120,
      sortable: false,
      filterable: false,
    },
    {
      field: "issuedDate",
      headerName: "Issued Date",
      width: 120,
      sortable: false,
      filterable: false,
    },
    {
      field: "action",
      headerName: "Action",
      width: 220,
      editable: false,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<unknown, InvoiceRow>) => (
        <Stack direction="row" spacing={0.5}>
          <Button
            color="primary"
            size="large"
            disableFocusRipple
            tabIndex={-1}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              handle.navigateToInvoicePage(String(params.row.id));
            }}
            sx={{ "&:focus": { outline: "none" }, "&.Mui-focusVisible": { outline: "none" } }}
          >
            {params.row?.status?.toLocaleLowerCase() === INVOICE_STATUS.draft ? (
              <EditRoundedIcon />
            ) : (
              <VisibilityRoundedIcon />
            )}
          </Button>
        </Stack>
      ),
    },
  ];

  useEffect(() => {
    setRowCountState((prevRowCountState) => invoices?.totalDocs ?? prevRowCountState);
  }, [invoices?.totalDocs]);

  useEffect(() => {
    if (Array.isArray(invoices?.list)) {
      const invoicesRows = invoices.list.map((doc) => {
        const createdAt = doc?.createdAt ? new Date(doc?.createdAt) : "";
        return {
          id: doc?._id,
          cursor: invoices?.page,
          organisation: doc?.tenancyBillingConfig?.clientConnection?.name,
          tenantName: doc?.tenancyBillingConfig?.firstName + " " + doc?.tenancyBillingConfig?.lastName || "-",
          total: doc?.total ?? 0,
          status: startCase(doc?.status) || "-",
          type: startCase(doc?.type) || "-",
          month: INVOICE_BILLING_MONTHS.find((month) => month.value === doc?.month)?.label,
          year: doc?.year,
          issuedDate: doc?.issuedDate ? moment(doc?.issuedDate).format("D-M-YY") : "-",
          createdAt: createdAt
            ? {
                date: moment(createdAt).format("D-M-YY"),
                time: moment(createdAt).format("h:mm a"),
              }
            : {
                date: "",
                time: "",
              },
        };
      });

      setRows(invoicesRows);
    } else {
      setRows([]);
    }
  }, [invoices]);

  useEffect(() => {
    if (params?.offset) {
      handle.getAllInvoicesFn({
        cursor: params?.offset,
        search: searchTerm,
        status: invoiceStatusFilterRef.current,
        type: invoiceTypeFilterRef.current,
      });
      dispatch(saveValue({ page: params?.offset, limit: params?.limit }));
    }
  }, [params?.offset, params?.sort, params?.limit]);

  const debounced = useCallback(debounce(handle.handleSearch, 500), []);

  return (
    <Grid container direction="column" spacing={0} style={{ padding: `20px` }}>
      <Grid
        style={{
          borderBottom: "1px solid rgba(224, 224, 224, 1)",
          display: "flex",
          alignItems: "center",
          columnGap: "50px",
        }}
      >
        <Typography
          level="h1"
          style={{
            fontSize: "24px",
            fontWeight: 800,
            letterSpacing: "-0.5px",
          }}
        >
          Invoices
        </Typography>
        <SearchWrapper>
          <SearchFormControl>
            <Input
              className="input-border-bottom"
              id="invoice-search"
              autoComplete="search"
              placeholder="Search…"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                debounced(e.target.value);
              }}
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              }
              endAdornment={
                searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => {
                        setSearchTerm("");
                        searchTerm !== "" && debounced("");
                      }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }
            />
          </SearchFormControl>
        </SearchWrapper>
        <ButtonGroup variant="outlined">
          {invoicesStatusFilters.map((option) => (
            <Button
              key={option}
              size="small"
              onClick={() => {
                handle.handleStatusFilterChange(option);
              }}
              variant={invoiceStatusFilterRef.current === option ? "contained" : "outlined"}
            >
              {startCase(option)}
            </Button>
          ))}
        </ButtonGroup>
        <Stack style={{ marginBottom: "7px", flexGrow: 2 }}>
          <DatePicker
            selected={filterDate?.startDate}
            onChange={handle.onChangeDatePicker}
            startDate={filterDate?.startDate}
            endDate={filterDate?.endDate}
            selectsRange
            isClearable
            name="Select Date"
            monthsShown={2}
            maxDate={new Date()}
            customInput={<TextField label="Select Date" size="small" style={{ width: "100%" }} />}
            dateFormat={dateFormat}
          />
        </Stack>
        {/* Hide for Temporary will be enable when auto invoices are on */}
        {/* <ButtonGroup variant="outlined">
          {invoicesTypeFilters.map((option) => (
            <Button
              key={option}
              size="small"
              onClick={() => {
                handle.handleTypeFilterChange(option);
              }}
              variant={invoiceTypeFilterRef.current === option ? "contained" : "outlined"}
            >
              {startCase(option)}
            </Button>
          ))}
        </ButtonGroup> */}
      </Grid>

      <Grid>
        <DataGridTable
          onCellClick={(params: { field: string; id: string }) => {
            if (params.field !== "action" && params.field !== "status") {
              return handle.navigateToInvoicePage(params.id);
            }
          }}
          onRowClick={() => {}}
          data={rows || []}
          columns={invoicesColumns}
          rowsPerPageOptions={[10, 20, 50, 100]}
          pageSize={pageSizeRef?.current || 10}
          rowCount={rowCountState}
          params={params}
          setParams={setParams}
          page={page}
          isLoading={gettingInvoices}
          pageSizeRef={pageSizeRef}
          disableColumnFilter
        />
      </Grid>
    </Grid>
  );
});

export default InvoiceList;
